import { useMemo } from "react";
import { ObraChecklistServicoCampos } from "../../../../domain/models/obra_checklist_servico_campos";
import { InspecaoObraDetalhes } from "../../../../domain/models/inspecao_obra_detalhes";
import { FuncionarioDropDown } from "../../../../../../common/domain/models/funcionario_dropdown";
import { UseFormReturn } from "react-hook-form";
import { InspecaoObraVerificacaoForm } from "../../../schemas/ObraItensChecklistArea_verificacao/inspecao_obra_verificacao";
import { FormDatePickerInput } from "../../../../../../common/components/masked_inputs/react_forms/form_date_picker_input";
import { DefaultFormCheckBox } from "../../../../../../common/components/default_form_checkbox";
import { DefaultFormAutocomplete } from "../../../../../../common/components/default_form_autocomplete";
import { FormRadioGroupInput } from "../../../../../../common/components/masked_inputs/react_forms/form_radio_group_input";
import { ObraHeaderInfo } from "../../../../domain/models/obra_header_info";

import CascateList from "../../../../../../common/components/cascate_list";
import dayjs from "dayjs";
import DefaultFormInput from "../../../../../../common/components/default_form_input";

/**
 * InformacoesGeraisPanel
 */

type Props = {
  statusChecklistServico: number | null;
  lockFuncionarioInspecionado: boolean;
  lockFuncionarioAprovado: boolean;
  selectedObraHeaderInfo: ObraHeaderInfo | null;
  obraChecklistServicoCampos: ObraChecklistServicoCampos;
  inspecaoObra: InspecaoObraDetalhes;
  funcionariosDropDown: FuncionarioDropDown[];
  hookForm: UseFormReturn<InspecaoObraVerificacaoForm>;
};

export const InformacoesGeraisPanel = ({
  statusChecklistServico,
  lockFuncionarioInspecionado,
  lockFuncionarioAprovado,
  selectedObraHeaderInfo,
  obraChecklistServicoCampos,
  inspecaoObra,
  funcionariosDropDown,
  hookForm,
}: Props) => {
  const [
    filtrarFuncionariosPorCentroCusto,
    idFuncionarioInspecionado,
    idFuncionarioAprovado,
  ] = hookForm.watch([
    'filtrarFuncionariosPorCentroCusto',
    'idFuncionarioInspecionado',
    'idFuncionarioAprovado',
  ]);

  const inspecionadosDropDownFiltrados = useMemo(() => {
    const inspecionados = funcionariosDropDown ?? [];
    return filtrarFuncionariosPorCentroCusto && selectedObraHeaderInfo
      ? inspecionados.filter(x => {
        if (idFuncionarioInspecionado && x.idFuncionario === idFuncionarioInspecionado) {
          return true;
        }
        if (x.canBeSelected !== true) {
          return false;
        }
        return x.idCentroCusto === selectedObraHeaderInfo.idCentroCusto;
      })
      : inspecionados.filter(x => {
        if (idFuncionarioInspecionado && x.idFuncionario === idFuncionarioInspecionado) {
          return true;
        }
        if (x.canBeSelected !== true) {
          return false;
        }
        return true;
      });
  }, [
    selectedObraHeaderInfo,
    funcionariosDropDown,
    filtrarFuncionariosPorCentroCusto,
    idFuncionarioInspecionado,
  ]);

  const aprovadosDropDownFiltrados = useMemo(() => {
    const aprovados = funcionariosDropDown ?? [];
    return filtrarFuncionariosPorCentroCusto && selectedObraHeaderInfo
      ? aprovados.filter(x => {
        if (idFuncionarioAprovado && x.idFuncionario === idFuncionarioAprovado) {
          return true;
        }
        if (x.canBeSelected !== true) {
          return false;
        }
        return x.idCentroCusto === selectedObraHeaderInfo.idCentroCusto;
      })
      : aprovados.filter(x => {
        if (idFuncionarioAprovado && x.idFuncionario === idFuncionarioAprovado) {
          return true;
        }
        if (x.canBeSelected !== true) {
          return false;
        }
        return true;
      });
  }, [
    selectedObraHeaderInfo,
    funcionariosDropDown,
    filtrarFuncionariosPorCentroCusto,
    idFuncionarioAprovado,
  ]);

  const statusOptions = useMemo(() => {
    return [
      {
        id: 0,
        label: 'Verificação Não Iniciada',
        style: {
          backgroundColor: '#f8cb00',
          color: 'white',
          padding: '.2rem .4rem',
          fontSize: '0.85rem',
          fontWeight: 700,
          textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000',
          borderRadius: '2px',
        },
      },
      {
        id: 3,
        label: 'Verificação Iniciada',
        style: {
          backgroundColor: '#89c4f4',
          color: 'white',
          padding: '.2rem .4rem',
          fontSize: '0.85rem',
          fontWeight: 700,
          textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000',
          borderRadius: '2px',
        },
      },
      {
        id: 1,
        label: 'Encerrada',
        style: {
          backgroundColor: '#f3565d',
          color: 'white',
          padding: '.2rem .4rem',
          fontSize: '0.85rem',
          fontWeight: 700,
          textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000',
          borderRadius: '2px',
        },
      },
      {
        id: 2,
        disabled: true,
        label: 'Aprovado',
        style: {
          backgroundColor: '#45b6af',
          color: 'white',
          padding: '.2rem .4rem',
          fontSize: '0.85rem',
          fontWeight: 700,
          textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000',
          borderRadius: '2px',
        },
      },
    ];
  }, [
  ]);

  return (
    <CascateList title="Informações Gerais" defaultOpen>
      <div className="w-full flex flex-col gap-2">
        <div className="text-black dark:text-white">
          <span className="font-bold me-2">Data abertura:</span>
          <span>{inspecaoObra.dataAbertura && dayjs(inspecaoObra.dataAbertura).format('DD/MM/YYYY')}</span>
        </div>
        <div className="grid grid-cols-2">
          {
            (obraChecklistServicoCampos.labelCampo1 && obraChecklistServicoCampos.labelCampo1.length > 0) &&
            <div className="text-black dark:text-white">
              <span className="font-bold me-2">{obraChecklistServicoCampos.labelCampo1}:</span>
              <span className="font-base">{inspecaoObra.campo1}</span>
            </div>
          }
          {
            (obraChecklistServicoCampos.labelCampo2 && obraChecklistServicoCampos.labelCampo2.length > 0) &&
            <div className="text-black dark:text-white">
              <span className="font-bold me-2">{obraChecklistServicoCampos.labelCampo2}:</span>
              <span className="font-base">{inspecaoObra.campo2}</span>
            </div>
          }
          {
            (obraChecklistServicoCampos.labelCampo3 && obraChecklistServicoCampos.labelCampo3.length > 0) &&
            <div className="text-black dark:text-white">
              <span className="font-bold me-2">{obraChecklistServicoCampos.labelCampo3}:</span>
              <span className="font-base">{inspecaoObra.campo3}</span>
            </div>
          }
          {
            (obraChecklistServicoCampos.labelCampo4 && obraChecklistServicoCampos.labelCampo4.length > 0) &&
            <div className="text-black dark:text-white">
              <span className="font-bold me-2">{obraChecklistServicoCampos.labelCampo4}:</span>
              <span className="font-base">{inspecaoObra.campo4}</span>
            </div>
          }
        </div>
        <div className="grid md:grid-cols-1 lg:grid-cols-2 gap-4">
          <DefaultFormInput
            name='funcionarioAprovacaoFinal'
            label='Aprovação Final'
            readOnly={true}
            reactHookForm={hookForm}
          />

          <FormDatePickerInput
            name='dataAprovacaoFinal'
            label='Data da Aprovação Final'
            readOnly={true}
            reactHookForm={hookForm}
          />
        </div>
        <div className="grid md:grid-cols-1 lg:grid-cols-2 gap-4 items-start">
          <div>
            <DefaultFormCheckBox
              name='filtrarFuncionariosPorCentroCusto'
              label='Exibir por centro de custo?'
              readonly={lockFuncionarioAprovado || statusChecklistServico === 1}
              reactHookForm={hookForm}
            />

            <DefaultFormAutocomplete
              name='idFuncionarioInspecionado'
              label='Inspecionado por?'
              readOnly={lockFuncionarioInspecionado || statusChecklistServico === 1}
              options={inspecionadosDropDownFiltrados}
              getOptionDisabled={x => !x.canBeSelected}
              getOptionLabel={x => x.nome}
              getOptionKey={x => x.idFuncionario}
              reactHookForm={hookForm}
            />
          </div>

          <div className="mt-[42px]">
            <DefaultFormAutocomplete
              name='idFuncionarioAprovado'
              label='Aprovado por?'
              readOnly={lockFuncionarioAprovado || statusChecklistServico === 1}
              options={aprovadosDropDownFiltrados}
              getOptionDisabled={x => !x.canBeSelected}
              getOptionLabel={x => x.nome}
              getOptionKey={x => x.idFuncionario}
              reactHookForm={hookForm}
            />
          </div>
        </div>

        <div className="grid md:grid-cols-1 lg:grid-cols-3 gap-4 items-center">
          <div className="lg:col-span-2">
            <FormRadioGroupInput
              name='status'
              label='Status'
              readOnly={statusChecklistServico === 1}
              options={statusOptions}
              reactHookForm={hookForm}
            />
          </div>
          <div className="lg:col-span-1">
            <FormDatePickerInput
              name='dataEncerramento'
              label='Data de Encerramento'
              readOnly={lockFuncionarioAprovado || statusChecklistServico === 1}
              reactHookForm={hookForm}
            />
          </div>
        </div>
      </div>
    </CascateList>
  );
}